<template>
  <el-dialog
    :visible.sync="visible"
    :title="editingType ? editingType.name : ($tc('tournament.add_document_type') || 'Add Document Type')"
    width="90%"
    :close-on-click-modal="false"
    @close="handleClose">
    <div v-if="step === 1" class="step-one">
      <el-form label-position="top">
        <el-form-item :label="$tc('general.name')">
          <el-input v-model="name" :placeholder="$tc('tournament.document_type_name_placeholder') || 'e.g. Akreditacija'" />
        </el-form-item>
        <el-form-item :label="$tc('tournament.certificate_image') || 'Image'">
          <input type="file" accept="image/*" @change="handleFileSelect">
          <div v-if="previewUrl" class="preview">
            <img :src="previewUrl">
          </div>
        </el-form-item>
      </el-form>
    </div>

    <div v-else-if="step === 2" class="step-two">
      <div class="step-two-body">
        <div class="canvas-container">
          <canvas ref="canvas" class="document-canvas" />
        </div>
        <div class="variable-palette">
          <p class="palette-title">{{ $tc('tournament.variables') || 'Variable' }}</p>
          <el-button
            v-for="key in variableKeys"
            :key="key"
            size="small"
            class="variable-button"
            @mousedown.native.prevent
            @click="insertVariable(key)">
            {{ variableLabel(key) }}
          </el-button>
          <el-button
            size="small"
            type="primary"
            class="add-field-button"
            @mousedown.native.prevent
            @click="addTextField()">
            {{ $tc('tournament.add_text_field') || '+ Dodaj tekst' }}
          </el-button>

          <template v-if="selectedField">
            <p class="palette-title field-properties-title">{{ $tc('tournament.field_properties') || 'Podešavanja teksta' }}</p>
            <el-form label-position="top" size="small" class="field-properties">
              <el-form-item :label="$tc('tournament.text_align') || 'Poravnanje'">
                <el-radio-group v-model="panelTextAlign" @change="v => updateFieldProp('textAlign', v)">
                  <el-radio-button label="left">{{ $tc('tournament.align_left') || 'Levo' }}</el-radio-button>
                  <el-radio-button label="center">{{ $tc('tournament.align_center') || 'Sredina' }}</el-radio-button>
                  <el-radio-button label="right">{{ $tc('tournament.align_right') || 'Desno' }}</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item :label="$tc('tournament.font_size') || 'Veličina fonta (% visine slike)'">
                <el-input-number v-model="panelFontSize" :min="0.5" :max="20" :step="0.1" @change="updateFontSizePercent" />
              </el-form-item>
              <el-form-item :label="$tc('tournament.text_color') || 'Boja teksta'">
                <el-color-picker v-model="panelColor" @change="v => updateFieldProp('fill', v || '#000000')" />
              </el-form-item>
              <el-form-item :label="$tc('tournament.line_height') || 'Razmak između linija'">
                <el-input-number v-model="panelLineHeight" :min="0.8" :max="3" :step="0.1" @change="v => updateFieldProp('lineHeight', v)" />
              </el-form-item>
              <p class="field-hint">{{ $tc('tournament.drag_edge_to_resize') || 'Prevuci gornju/donju ivicu polja da promeniš visinu okvira.' }}</p>
              <el-button size="small" type="danger" plain @mousedown.native.prevent @click="deleteSelectedField">
                {{ $tc('general.delete') }}
              </el-button>
            </el-form>
          </template>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <el-button @click="handleClose">{{ $tc('general.cancel') }}</el-button>
      <el-button v-if="step === 1" type="primary" :disabled="!canProceed" :loading="creating" @click="handleNext">
        {{ $tc('general.next') || 'Next' }}
      </el-button>
      <el-button v-if="step === 2" type="primary" :loading="saving" :disabled="!fieldsLoaded" @click="handleSave">
        {{ $tc('general.save') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { VARIABLE_KEYS, SR_TOKENS } from '@/utils/documentTemplate.js'

export default {
  name: 'AddDocumentTypeModal',
  props: {
    value: { type: Boolean, default: false },
    tournamentId: { type: [Number, String], default: null },
    seminarId: { type: [Number, String], default: null },
    editingType: { type: Object, default: null }
  },
  data () {
    return {
      step: 1,
      name: '',
      selectedFile: null,
      previewUrl: null,
      creating: false,
      createdType: null,
      canvas: null,
      imageUrl: null,
      variableKeys: VARIABLE_KEYS,
      saving: false,
      // Gates the Save button so it cannot fire before loadExistingFields()
      // has actually populated the canvas (or, for a brand-new type with no
      // saved fields, before the canvas/image is ready). Stays false while a
      // background-image load is pending or has failed, so a slow/broken
      // image load can never result in an empty `fields: []` overwrite of a
      // previously-saved layout. See initCanvas()/loadExistingFields().
      fieldsLoaded: false,
      // Currently-selected Fabric text field (or null) and the property
      // panel's mirrored values, shown while a field is selected.
      selectedField: null,
      panelTextAlign: 'center',
      panelFontSize: 3,
      panelColor: '#000000',
      panelLineHeight: 1.2
    }
  },
  computed: {
    visible: {
      get () { return this.value },
      set (val) { this.$emit('input', val) }
    },
    canProceed () {
      return !!this.name.trim() && !!this.selectedFile
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.step = this.editingType ? 2 : 1
        this.name = this.editingType?.name || ''
        this.createdType = this.editingType || null
        this.selectedFile = null
        this.previewUrl = null
        this.selectedField = null
      }
    },
    step (newStep) {
      if (newStep === 2) {
        // Reset synchronously (not inside initCanvas) so the Save button -
        // which becomes visible the instant step 2 renders - is disabled
        // for the entire window before $nextTick's initCanvas() call, not
        // just once the image-load actually starts.
        this.fieldsLoaded = false
        this.$nextTick(() => this.initCanvas())
      }
    }
  },
  beforeDestroy () {
    this.disposeCanvas()
  },
  methods: {
    handleFileSelect (event) {
      const file = event.target.files?.[0]
      if (!file) return
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.selectedFile = file
      this.previewUrl = URL.createObjectURL(file)
    },
    async handleNext () {
      this.creating = true
      try {
        const result = this.seminarId
          ? await this.$store.dispatch('seminars/createDocumentType', {
            seminarId: this.seminarId, name: this.name.trim(), imageFile: this.selectedFile
          })
          : await this.$store.dispatch('tournaments/createDocumentType', {
            tournamentId: this.tournamentId, name: this.name.trim(), imageFile: this.selectedFile
          })
        if (result.success) {
          this.createdType = result.item
          this.step = 2
        } else {
          this.$message.error(result.error || 'Failed to create document type')
        }
      } catch (error) {
        this.$message.error('Failed to create: ' + (error.response?.data?.error || error.message))
      } finally {
        this.creating = false
      }
    },
    variableLabel (key) {
      const labels = {
        competitorName: this.$tc('tournament.competitor_name') || 'Ime takmičara',
        club: this.$tc('general.club', 1) || 'Klub',
        competition: this.$tc('tournament.competition') || 'Naziv turnira',
        position: this.$tc('tournament.place_position') || 'Pozicija',
        discipline: this.$tc('tournament.discipline') || 'Disciplina',
        category: this.$tc('tournament.category') || 'Kategorija',
        belt: this.$tc('form.belt') || 'Pojas',
        role: this.$tc('tournament.role') || 'Uloga'
      }
      return labels[key] || key
    },

    insertVariable (key) {
      if (!this.canvas) return
      const active = this.canvas.getActiveObject()
      const token = `{{${SR_TOKENS[key] || key}}}`
      if (active && active.isDocumentField) {
        if (!active.isEditing) {
          active.enterEditing()
          active.selectAll()
        }
        const cursor = active.selectionStart ?? active.text.length
        active.insertChars(token, undefined, cursor)
        active.setSelectionStart(cursor + token.length)
        active.setSelectionEnd(cursor + token.length)
        this.canvas.renderAll()
      } else {
        this.addTextField({
          template: token,
          left: 50,
          top: 50,
          width: 300,
          height: 80,
          fontSizePercent: 3,
          fontFamily: 'Arial',
          textAlign: 'center',
          lineHeight: 1.2,
          fill: '#000000'
        })
      }
    },

    async handleSave () {
      if (!this.canvas) return
      this.saving = true
      try {
        const fields = this.canvas.getObjects()
          .filter(obj => obj.isDocumentField)
          .map((obj, index) => ({
            id: `f${index + 1}`,
            template: obj.text,
            left: obj.left,
            top: obj.top,
            width: obj.width,
            // boxHeight (not obj.height) is the admin-set clip box height -
            // obj.height is Fabric's own auto-grown value based on how many
            // lines of text currently fit, which is not what should be
            // persisted. Corner/edge scaling is locked (see addTextField),
            // so scaleX/scaleY below are always 1 for fields touched here;
            // legacy fields loaded with a historical scale get normalized
            // into width/boxHeight/fontSize on load (see addTextField) and
            // are re-saved with scaleX/scaleY = 1 the next time this modal
            // saves, regardless of which field the admin actually edited.
            height: obj.boxHeight || obj.height,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
            angle: obj.angle || 0,
            // Stored as a percentage of the image's height, not raw
            // pixels, so the same authored value looks the same size
            // regardless of what resolution image this document type
            // uses - see addTextField() for the legacy-pixel conversion
            // applied on load to fields saved before this change.
            fontSizePercent: obj.fontSizePercent,
            fontFamily: obj.fontFamily,
            textAlign: obj.textAlign,
            lineHeight: obj.lineHeight || 1.2,
            fill: obj.fill
          }))

        const result = this.seminarId
          ? await this.$store.dispatch('seminars/updateDocumentTypeFields', {
            seminarId: this.seminarId, typeId: this.createdType.id, fields
          })
          : await this.$store.dispatch('tournaments/updateDocumentTypeFields', {
            tournamentId: this.tournamentId, typeId: this.createdType.id, fields
          })

        if (result.success) {
          this.$message.success(this.$tc('general.saved'))
          this.visible = false
          this.$emit('saved')
        } else {
          this.$message.error(result.error || 'Failed to save fields')
        }
      } catch (error) {
        this.$message.error('Failed to save: ' + (error.response?.data?.error || error.message))
      } finally {
        this.saving = false
      }
    },

    handleClose () {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.visible = false
      if (this.createdType) {
        this.$emit('saved')
      }
      // Reset step back to 1 so the `step` watcher always fires (1 -> 2) the
      // next time this modal is reopened for editing, even when reopened for
      // a *different* document type right after closing on step 2 — the
      // parent (DocumentTypesPanel) keeps a single persistent modal instance
      // and doesn't clear `editingType` on close, so without this reset
      // `step` would stay at 2 across edits and the watcher (Vue only fires
      // on value change) would never re-run initCanvas() for the new type.
      this.step = 1
      this.selectedField = null
      if (!this.editingType) {
        this.createdType = null
      }
    },
    resolveImageUrl () {
      const baseURL = this.$config?.baseURLAPI || (this.$config?.baseURL ? this.$config.baseURL + '/api' : '') || process.env.VUE_APP_API_URL || ''
      const path = this.seminarId
        ? `seminar/${this.seminarId}/document-types/${this.createdType.id}/image`
        : `tournament/${this.tournamentId}/document-types/${this.createdType.id}/image`
      return `${baseURL}/${path}`
    },

    initCanvas () {
      // Save must stay disabled until loadExistingFields() has genuinely
      // completed for THIS load (or has failed) - never leave the previous
      // load's `true` value dangling across re-init.
      this.fieldsLoaded = false

      if (!window.fabric) {
        this.$message.error('Fabric.js library is not available.')
        return
      }
      if (this.canvas) {
        this.disposeCanvas()
      }
      this.canvas = new window.fabric.Canvas(this.$refs.canvas, {
        selection: true,
        preserveObjectStacking: true,
        backgroundColor: '#ffffff'
      })
      this.canvas.on('selection:created', (e) => this.onSelectionChange((e.selected && e.selected[0]) || this.canvas.getActiveObject()))
      this.canvas.on('selection:updated', (e) => this.onSelectionChange((e.selected && e.selected[0]) || this.canvas.getActiveObject()))
      this.canvas.on('selection:cleared', () => this.onSelectionChange(null))
      // Dragging the native left/right width handles changes `width`, which
      // is one of Textbox's dimension-affecting props - Fabric rewraps the
      // text and recomputes `height` from the new line count, silently
      // undoing our fixed boxHeight (visually: the box appears to reset its
      // height the moment width changes). Both our custom top/bottom
      // handles and Fabric's built-in left/right handles share the
      // 'resizing' actionName, so canvas fires 'object:resizing' for all of
      // them - re-pinning height here after every tick covers every handle
      // in one place.
      this.canvas.on('object:resizing', (e) => {
        if (e.target && e.target.isDocumentField) this.syncFieldHeight(e.target)
      })

      this.imageUrl = this.resolveImageUrl()
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const w = img.naturalWidth || img.width
        const h = img.naturalHeight || img.height
        this.canvas.setWidth(w)
        this.canvas.setHeight(h)
        window.fabric.Image.fromURL(this.imageUrl, (fabricImg) => {
          fabricImg.set({
            selectable: false,
            evented: false,
            lockMovementX: true,
            lockMovementY: true,
            lockRotation: true,
            lockScalingX: true,
            lockScalingY: true,
            hasControls: false,
            hasBorders: false
          })
          this.canvas.setBackgroundImage(fabricImg, () => {
            this.canvas.renderAll()
            this.loadExistingFields()
            // Fields (if any) are now on the canvas - Save can safely read
            // canvas.getObjects() without risking an empty-fields overwrite.
            this.fieldsLoaded = true
          })
        }, { crossOrigin: 'anonymous' })
      }
      img.onerror = () => {
        this.$message.error('Failed to load document image')
        // Leave fieldsLoaded false: no layout was loaded, so Save must stay
        // disabled rather than being able to persist fields: [].
      }
      img.src = this.imageUrl
    },

    loadExistingFields () {
      const fields = this.editingType?.fields || []
      fields.forEach(field => this.addTextField(field))
    },

    addTextField (field = null) {
      if (!this.canvas) return
      const canvasWidth = this.canvas.getWidth()
      const canvasHeight = this.canvas.getHeight()
      const defaults = {
        left: (canvasWidth - 300) / 2,
        top: (canvasHeight - 80) / 2,
        width: 300,
        height: 80,
        fontFamily: 'Arial',
        textAlign: 'center',
        lineHeight: 1.2,
        fill: '#000000'
      }
      const layout = { ...defaults, ...(field || {}) }

      // Fields saved before this change could carry non-uniform scaleX/
      // scaleY from the old free-scale corner handles. Editing scaled text
      // is where Fabric's cursor/selection math gets unreliable (backspace
      // removing more than what's selected) - bake any existing scale into
      // width/height once on load so scaleX/scaleY become 1 for every
      // field touched from here on. The effective print size
      // (width*scaleX, height*scaleY) is unchanged, so this doesn't alter
      // how an already-configured certificate prints.
      const scaleX = layout.scaleX || 1
      const scaleY = layout.scaleY || 1
      const width = layout.width * scaleX
      const height = layout.height * scaleY

      // fontSizePercent (percentage of the image's height) is the only
      // format going forward - Fabric itself still needs an absolute pixel
      // fontSize to render with, so convert on the way in. Fields saved
      // before this change only have the old absolute-pixel `fontSize`
      // (plus, for the oldest ones, the legacy scaleY above); bake both
      // into an equivalent pixel size once here, then derive the
      // fontSizePercent that reproduces it on THIS image - so an
      // already-configured certificate keeps printing exactly as it does
      // now, and is upgraded to the new format the next time it's saved.
      const imageHeight = canvasHeight
      let fontSizePercent = layout.fontSizePercent
      let fontSizePx
      if (fontSizePercent != null) {
        fontSizePx = (fontSizePercent / 100) * imageHeight
      } else {
        fontSizePx = (layout.fontSize || 28) * scaleY
        fontSizePercent = (fontSizePx / imageHeight) * 100
      }

      const textbox = new window.fabric.Textbox(field ? field.template : `Text {{${SR_TOKENS.competitorName}}}`, {
        left: layout.left,
        top: layout.top,
        width,
        height,
        angle: layout.angle || 0,
        fontSize: fontSizePx,
        fontFamily: layout.fontFamily,
        textAlign: layout.textAlign,
        lineHeight: layout.lineHeight,
        fill: layout.fill,
        isDocumentField: true,
        boxHeight: height,
        fontSizePercent,
        // Rotation is no longer editable - fields keep whatever angle they
        // already had (still respected at print time), but can't be
        // rotated further from the editor.
        lockRotation: true,
        // Fabric caches a rendered bitmap of the object and only rebuilds
        // it when a property in Textbox's own _dimensionAffectingProps list
        // changes - that list has 'width' but not 'height', so our own
        // syncFieldHeight() correcting `height` back to `boxHeight` after a
        // width-drag (see makeHeightControl/'object:resizing' below) didn't
        // invalidate the cache. The next paint then stretched the stale
        // (pre-correction, taller) cached bitmap into the new, shorter box,
        // which is what made text appear to float above the box's top edge
        // during/after a width drag. Disabling the cache for these few
        // small text fields is a negligible cost and guarantees every
        // render reflects the field's current, corrected dimensions.
        objectCaching: false
      })
      // Corner scale handles (the source of the old non-uniform scaleX/
      // scaleY distortion) and rotation are removed entirely by hiding
      // their controls below - NOT via lockScalingX/Y. Textbox's ml/mr
      // width handles share Fabric's scale cursor/lock plumbing, so
      // setting those lock flags also disables ml/mr (shows a "not
      // allowed" cursor and blocks the drag), which we still need. Hiding
      // the corner controls is enough on its own: an invisible control
      // can't be grabbed regardless of lock flags. Assigning a fresh
      // controls object (not mutating the shared fabric.Textbox.prototype.
      // controls) keeps this instance-only.
      textbox.controls = {
        ...textbox.controls,
        mt: this.makeHeightControl('top'),
        mb: this.makeHeightControl('bottom')
      }
      textbox.setControlsVisibility({ mt: true, mb: true, tl: false, tr: false, bl: false, br: false, mtr: false })
      // Fabric's Textbox recalculates its own `height` from the wrapped
      // line count on every text/font/line-height change (typing, or the
      // property panel's font size / line height controls) - left alone,
      // that silently overwrites our fixed `boxHeight`, which is what made
      // the visible selection border/handles look frozen (they're drawn
      // from Fabric's `height`, not `boxHeight`). Re-pin it after every
      // such change so the box you see always matches boxHeight.
      textbox.on('changed', () => this.syncFieldHeight(textbox))
      this.syncFieldHeight(textbox)

      this.canvas.add(textbox)
      this.canvas.setActiveObject(textbox)
      this.canvas.renderAll()
    },

    // Custom top/bottom control replacing Fabric.Textbox's default (hidden)
    // mt/mb handles: dragging adjusts our own `boxHeight` directly instead
    // of Fabric's scaleY, keeping the opposite edge fixed in canvas space.
    // Coordinates are converted into the field's own unrotated local space
    // (relative to its top-left corner, which is also where Fabric pivots
    // rotation for a left/top-origin object) so this stays correct for the
    // rare legacy field that still carries a saved rotation angle.
    makeHeightControl (edge) {
      return new window.fabric.Control({
        x: 0,
        y: edge === 'top' ? -0.5 : 0.5,
        cursorStyle: 'ns-resize',
        actionName: 'resizing',
        actionHandler: (eventData, transform, x, y) => {
          const target = transform.target
          const angleRad = ((target.angle || 0) * Math.PI) / 180
          const cos = Math.cos(angleRad)
          const sin = Math.sin(angleRad)
          const dx = x - target.left
          const dy = y - target.top
          const localDy = -dx * sin + dy * cos

          if (edge === 'bottom') {
            target.set('boxHeight', Math.max(20, Math.round(localDy)))
          } else {
            const newHeight = Math.max(20, Math.round(target.boxHeight - localDy))
            const heightDelta = newHeight - target.boxHeight
            target.set({
              left: target.left + heightDelta * sin,
              top: target.top - heightDelta * cos,
              boxHeight: newHeight
            })
          }
          this.syncFieldHeight(target)
          return true
        }
      })
    },

    // Keeps the object's own `height` (which drives the visible selection
    // border and control-handle positions) pinned to our fixed `boxHeight`,
    // and refreshes the clip + handle positions to match. Call this after
    // anything that could change either value.
    syncFieldHeight (obj) {
      obj.set('height', obj.boxHeight)
      this.applyClipPath(obj)
      obj.setCoords()
      // requestRenderAll (not renderAll) so this coalesces into the same
      // animation-frame paint Fabric's own transform pipeline schedules
      // right after this runs (it calls requestRenderAll once actionHandler
      // returns) - calling the synchronous renderAll here as well used to
      // force an extra, redundant paint on every single mousemove tick of a
      // drag, on top of Fabric's own.
      if (this.canvas) this.canvas.requestRenderAll()
    },

    // Clips rendering to the field's own box (width x boxHeight) so text
    // can never visually spill outside it, in the editor and - via the
    // matching ctx.clip() in PrintDocuments.renderField - when printed.
    // clipPath is defined in the object's own local coordinate space
    // (originX/Y 'center'), so it automatically follows the field's
    // position/rotation/scale without extra math here.
    applyClipPath (obj) {
      if (!obj.clipPath) {
        obj.clipPath = new window.fabric.Rect({ originX: 'center', originY: 'center' })
      }
      obj.clipPath.set({ width: obj.width, height: obj.boxHeight || obj.height })
    },

    onSelectionChange (obj) {
      if (obj && obj.isDocumentField) {
        this.selectedField = obj
        this.panelTextAlign = obj.textAlign || 'center'
        this.panelFontSize = obj.fontSizePercent || 3
        this.panelColor = obj.fill || '#000000'
        this.panelLineHeight = obj.lineHeight || 1.2
      } else {
        this.selectedField = null
      }
    },

    updateFieldProp (prop, value) {
      if (!this.selectedField || !this.canvas) return
      this.selectedField.set(prop, value)
      // lineHeight/textAlign are in Fabric's Textbox _dimensionAffectingProps
      // list, so setting either triggers Fabric to recompute `height` from
      // the (possibly rewrapped) text - re-pin it back to boxHeight, same
      // as after typing or dragging.
      this.syncFieldHeight(this.selectedField)
    },

    // Font size is authored as a percentage of the image's height (see
    // addTextField), but Fabric's own `fontSize` still needs actual pixels
    // to render with - convert here using the current canvas height (which
    // is set to the image's natural height in initCanvas).
    updateFontSizePercent (percent) {
      if (!this.selectedField || !this.canvas) return
      this.selectedField.set({
        fontSizePercent: percent,
        fontSize: (percent / 100) * this.canvas.getHeight()
      })
      this.syncFieldHeight(this.selectedField)
    },

    deleteSelectedField () {
      if (!this.selectedField || !this.canvas) return
      this.canvas.remove(this.selectedField)
      this.selectedField = null
      this.canvas.renderAll()
    },

    disposeCanvas () {
      if (this.canvas) {
        try {
          this.canvas.dispose()
        } catch (error) {
          console.error('AddDocumentTypeModal: error disposing canvas', error)
        }
        this.canvas = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.step-one {
  .preview {
    margin-top: 10px;
    img {
      max-width: 300px;
      max-height: 200px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
    }
  }
}

.step-two {
  .document-canvas {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    background: #ffffff;
  }
}

.step-two-body {
  display: flex;
  gap: 20px;
  .canvas-container {
    flex: 1;
    overflow: auto;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 20px;
    background: #f5f5f5;
    max-height: 65vh;
  }
  .variable-palette {
    width: 220px;
    flex-shrink: 0;
    .palette-title {
      font-weight: 500;
      margin-bottom: 10px;
    }
    .variable-button, .add-field-button {
      display: block;
      width: 100%;
      margin: 0 0 8px 0;
    }
    .field-hint {
      font-size: 12px;
      color: #909399;
      margin: 4px 0 12px;
    }
  }
}
</style>
